import { isNumber, upperFirst } from "lodash";
import { renderNumberValue, getNumberFromNumberString, getDefaultMaxLength } from "./string";
import { MinMaxRestrictionsField } from "../store/knowledge-base-item/knowledge-base-item.types";
export const DEFAULT_MAX_LENGHT = 17;
export const composeValidators = (...validators) => (value, allValues) => validators.reduce((error, validator) => error || validator(value, allValues), undefined);
export const required = (entity) => (value) => {
    if (isNotEmpty(value, typeof value === "string")) {
        return undefined;
    }
    if (entity) {
        return `${entity} cannot be empty.`;
    }
    return "Mandatory field";
};
export const maxLength = (max, error) => (value = 0) => value.toString().length <= max ? undefined : (error ?? "Value is too long");
export const isNotEmpty = (value, shouldValidateEmptyString) => {
    return !(value === null || value === undefined || (shouldValidateEmptyString && value === ""));
};
export const formValidation = (values) => {
    const errors = {};
    if (!values) {
        return errors;
    }
    const min = +renderNumberValue(values.min, { roundingDisabled: true });
    const max = +renderNumberValue(values.max, { roundingDisabled: true });
    if (!min && min !== 0) {
        errors.min = "Must be a number";
    }
    else if (!max && max !== 0) {
        errors.max = "Must be a number";
    }
    else if (maxLength(getDefaultMaxLength(min.toString()))(min)) {
        errors.min = `The length of 'number' must be ${DEFAULT_MAX_LENGHT} characters or fewer. You entered ${min.toString().length} characters.`;
    }
    else if (maxLength(getDefaultMaxLength(max.toString()))(max)) {
        errors.max = `The length of 'number' must be ${DEFAULT_MAX_LENGHT} characters or fewer. You entered ${max.toString().length} characters.`;
    }
    else if (min >= max) {
        errors.min = "Min value cannot be bigger or equal to Max";
    }
    return errors;
};
export const isFormChanged = ({ valid, dirty }, id) => {
    return valid && (!isNotEmpty(id) || id === -1 || dirty);
};
//TODO make this method rerender component to change disabled parameter for button
export const isDisabled = (initialValues, values) => {
    Object.keys(values).map((key) => {
        if (values[key] !== initialValues[key]) {
            return false;
        }
    });
    return true;
};
export const minLength = (min, error) => (value = "") => value?.length >= min ? undefined : (error ?? "Value is too short");
export const isNumberValid = (value) => !isNaN(getNumberFromNumberString(value ?? "", false));
export const isNumberValidator = (isInteger) => (value) => isNumberValid(value) ? undefined : `The value must be ${isInteger ? "an integer" : "a number"}`;
export const isIntegerNumberValidator = (value) => {
    if (!isNotEmpty(value)) {
        return undefined;
    }
    return Number.isInteger(getNumberFromNumberString(value ?? "", false))
        ? undefined
        : `The value must be an integer`;
};
export const ACCEPTABLE_ERROR_MESSAGE = "Highest acceptable value must be greater than Lowest acceptable value";
export const MIN_MAX_ERROR_MESSAGE = "Max value must be greater than Min value";
export const MIN_MAX_EQUAL_ERROR_MESSAGE = "Max value must be greater than or equal to Min value";
export const fromToValidator = (from, to, canBeEqual = true, customErrorMessage) => {
    const numericFrom = getNumberFromNumberString(from ?? "", false);
    const numericTo = getNumberFromNumberString(to ?? "", false);
    if (isNotEmpty(from, true) &&
        isNotEmpty(to, true) &&
        !isNaN(numericFrom) &&
        !isNaN(numericTo) &&
        (canBeEqual ? numericFrom > numericTo : numericFrom >= numericTo)) {
        const message = canBeEqual ? MIN_MAX_EQUAL_ERROR_MESSAGE : MIN_MAX_ERROR_MESSAGE;
        return customErrorMessage || message;
    }
    return "";
};
export const isInRange = (value, from, to) => value >= from && value <= to;
export const minMax = (min, max, message, minEqual = true, maxEqual = true, isInteger = false) => (value) => {
    const numberValue = getNumberFromNumberString(value, false);
    const shouldValidateMin = min !== undefined && ((minEqual && numberValue < min) || (!minEqual && numberValue <= min));
    const shouldValidateMax = max !== undefined && ((maxEqual && numberValue > max) || (!maxEqual && numberValue >= max));
    if (shouldValidateMin || shouldValidateMax) {
        if (message) {
            return message;
        }
        let errorMessage = `The value must be ${isInteger ? "an integer" : "a number"}`;
        const formattedMin = renderNumberValue(min, { roundingDisabled: true });
        const formattedMax = renderNumberValue(max, { roundingDisabled: true });
        if (min !== undefined && max === undefined) {
            errorMessage = `${errorMessage} and greater than ${minEqual ? "or equal to" : ""} ${formattedMin}`;
        }
        else if (min === undefined && max !== undefined) {
            errorMessage = `${errorMessage} and less than ${maxEqual ? "or equal to" : ""} ${formattedMax}`;
        }
        else {
            if (minEqual && maxEqual) {
                errorMessage = `${errorMessage} between ${formattedMin} and ${formattedMax}`;
            }
            else if (!minEqual && !maxEqual) {
                errorMessage = `${errorMessage} greater than ${formattedMin} and less than ${formattedMax}`;
            }
            else if (!minEqual && maxEqual) {
                errorMessage = `${errorMessage} greater than ${formattedMin} and less than or equal to ${formattedMax}`;
            }
            else if (minEqual && !maxEqual) {
                errorMessage = `${errorMessage} greater than or equal to ${formattedMin} and less than ${formattedMax}`;
            }
        }
        return errorMessage;
    }
    return "";
};
export const getMinMaxRestrictionValue = (restrictions, key, unit, defaultAttribute) => {
    const valueRestrictions = restrictions?.[key];
    if (!isNotEmpty(valueRestrictions)) {
        return;
    }
    const unitToCompare = isNotEmpty(unit) && typeof unit === "object" && "value" in unit ? unit.value : (unit ?? null);
    if (Array.isArray(valueRestrictions)) {
        return valueRestrictions.find((item) => item.units === unitToCompare && item.attribute === defaultAttribute)?.value;
    }
    return isNotEmpty(valueRestrictions) &&
        typeof valueRestrictions === "object" &&
        "value" in valueRestrictions
        ? valueRestrictions.value
        : valueRestrictions;
};
export const getMinMaxRestrictions = (restrictions, unit, defaultAttribute) => Object.keys(MinMaxRestrictionsField).reduce((acc, key) => {
    const keyName = MinMaxRestrictionsField[key];
    const restrictionValue = getMinMaxRestrictionValue(restrictions, keyName, unit, defaultAttribute);
    if (isNotEmpty(restrictionValue)) {
        return { ...acc, [keyName]: restrictionValue };
    }
    else {
        return acc;
    }
}, {});
export const getMinMaxRestrictionsValidator = (restrictions, unit, defaultAttribute) => {
    const { MinValue, MinValueEqual, MaxValue, MaxValueEqual } = getMinMaxRestrictions(restrictions, unit, defaultAttribute);
    if (isNumber(MinValue) || isNumber(MaxValue) || isNumber(MinValueEqual) || isNumber(MaxValueEqual)) {
        return minMax(MinValueEqual ?? MinValue, MaxValueEqual ?? MaxValue, undefined, isNumber(MinValueEqual), isNumber(MaxValueEqual), restrictions?.IsInteger);
    }
};
export function getRestrictionsValidator(restrictions, label, customValidator) {
    const validators = [];
    const { IsMandatory, MaxLength, MinLength, IsNumber, IsInteger } = restrictions || {};
    if (IsMandatory) {
        validators.push(required(upperFirst(label)));
    }
    if (IsNumber) {
        validators.push(isNumberValidator(IsInteger));
    }
    if (isNumber(MinLength)) {
        validators.push(minLength(MinLength));
    }
    if (isNumber(MaxLength)) {
        validators.push(maxLength(MaxLength));
    }
    const minMaxValidator = getMinMaxRestrictionsValidator(restrictions);
    if (minMaxValidator) {
        validators.push(minMaxValidator);
    }
    if (IsInteger) {
        validators.push(isIntegerNumberValidator);
    }
    if (customValidator) {
        validators.push(customValidator);
    }
    return composeValidators(...validators);
}
